import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, OutlinedInput, RadioGroup, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languages } from '../../config/language.config';
import { useGlobalStateContext } from '../../context/GlobalStateContext/GlobalStateContext';
import { useSmsSetupContext } from '../../context/SmsSetupContext/SmsSetupContext';
import { useIsDesktop } from '../../utils/mediaQueries';
import { formatPhoneNumber, isValidPhoneNumber, stripFormat, } from '../../utils/phone-number.helper';
import { ActionButton } from '../Button/Button';
import { CustomCheckbox } from '../Checkbox/Checkbox';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { RadioButton } from '../RadioButton/RadioButton';
import styles from './styles';
export const SmsSetupStep1 = () => {
    const { t: text, i18n: { resolvedLanguage = 'en' } = {} } = useTranslation();
    // controlled state of the displayed phone number
    const [phoneNumber, setPhoneNumber] = useState({ value: '', error: false });
    const [displayedNumber, setDisplayedNumber] = useState(isValidPhoneNumber(phoneNumber.value) ? formatPhoneNumber(phoneNumber.value) : phoneNumber.value);
    const [selectedRadio, setSelectedRadio] = useState(resolvedLanguage);
    const [isConsentChecked, setIsConsentChecked] = useState(false);
    const [inSubmit, setInSubmit] = useState(false);
    const { handleSmsSetup, smsSetupContextState, setSmsSetupContextState } = useSmsSetupContext();
    const { showSnackBar } = useGlobalStateContext();
    const goBack = async () => {
        setSmsSetupContextState({
            ...smsSetupContextState,
            smsSetupCurrentStep: 0,
        });
    };
    const handleRadioChange = (e) => {
        setInSubmit(false);
        setSelectedRadio(e.target.value);
    };
    const handleOnBlur = (e) => {
        setInSubmit(false);
        const phoneNumberValue = e.target.value;
        // format the displayed number on blur
        if (isValidPhoneNumber(stripFormat(phoneNumberValue))) {
            setDisplayedNumber(formatPhoneNumber(phoneNumberValue));
        }
        setPhoneNumber({
            value: stripFormat(phoneNumberValue),
            error: !isValidPhoneNumber(stripFormat(phoneNumberValue)),
        });
    };
    const handleOnChange = (e) => {
        setInSubmit(false);
        const inputValue = e.target.value;
        let numberString = stripFormat(inputValue);
        if (numberString.length > 11)
            numberString = numberString.slice(0, 11);
        if (isNaN(Number(numberString)))
            return;
        setDisplayedNumber(formatPhoneNumber(numberString));
    };
    const handleSubmit = async () => {
        setPhoneNumber({
            value: stripFormat(phoneNumber.value),
            error: !isValidPhoneNumber(stripFormat(phoneNumber.value)),
        });
        setInSubmit(true);
        if (phoneNumber.error || !isConsentChecked || !selectedRadio)
            return false;
        setInSubmit(true);
        const userSmsInputs = {
            phoneNumber: phoneNumber.value,
            notifLanguage: `${selectedRadio.toUpperCase()}-CA`, // not sure why IBM wanted uppercase
            consent: isConsentChecked,
        };
        localStorage.setItem('sms-setup', JSON.stringify(userSmsInputs));
        try {
            await handleSmsSetup({
                isResend: false,
                ...userSmsInputs,
                notifLanguage: `${selectedRadio.toLowerCase()}-CA`, // BE needs lower case
            }, 0);
        }
        catch (e) {
            console.log('SMS setup error ', e);
        }
    };
    const isDesktop = useIsDesktop();
    useEffect(() => {
        const { success, reason, failureCode } = smsSetupContextState?.apiReply || {};
        if ((!success && reason) || failureCode) {
            showSnackBar(text('SET_UP_SMS_SERVER_ERROR'), 'error');
            setPhoneNumber({
                value: stripFormat(phoneNumber.value),
                error: 'SERVER-ERROR',
            });
        }
    }, [smsSetupContextState.apiReply]);
    return (_jsxs(Box, { sx: styles.pageContentWrapper, children: [isDesktop && (_jsxs(Box, { sx: { flex: '0' }, children: [_jsx(Typography, { variant: 'h1', sx: { mt: '2rem' }, children: text('SET_UP_SMS_TITLE') }), _jsx(Box, { sx: styles.subtitle, children: _jsx(Box, { children: _jsx(Typography, { variant: 'subtitle1', children: text('SET_UP_SMS_SUBTITLE') }) }) })] })), _jsx(Box, { children: _jsx(Typography, { variant: 'h2', sx: { mb: '0.5rem', mt: '1.5rem' }, children: text('SET_UP_SMS_TITLE2') }) }), _jsxs(Box, { mb: 2, children: [_jsx("label", { htmlFor: 'phoneNumber', children: _jsx(Typography, { mb: 1, variant: 'body2', children: text('SET_UP_SMS_PHONE_NUMBER') }) }), _jsx(OutlinedInput, { value: displayedNumber, error: false, fullWidth: true, onChange: handleOnChange, onBlur: handleOnBlur, sx: { background: 'white' }, name: 'phoneNumber', id: 'phoneNumber' }), phoneNumber.error && (_jsx(Box, { children: _jsx(ErrorMessage, { type: 'Plain', message: text(phoneNumber.error === 'SERVER-ERROR'
                                ? 'SET_UP_SMS_SERVER_ERROR'
                                : 'SET_UP_SMS_ERR_PHONE') }) }))] }), _jsxs(Box, { children: [_jsx(Typography, { variant: 'body1', children: text('SET_UP_SMS_LANGUAGE') }), _jsx(RadioGroup, { children: _jsxs(Box, { sx: { display: 'flex', gap: '4rem' }, children: [_jsx(RadioButton, { label: 'English', value: 'en', isChecked: selectedRadio === languages.en, onChange: (e) => {
                                        handleRadioChange(e);
                                    } }, 'english'), _jsx(RadioButton, { label: 'Fran\u00E7ais', isChecked: selectedRadio === languages.fr, value: 'fr', onChange: (e) => {
                                        handleRadioChange(e);
                                    } }, 'french')] }) }), !selectedRadio && inSubmit && (_jsx(Box, { children: _jsx(ErrorMessage, { type: 'Plain', message: text('SET_UP_SMS_ERR_LANGUAGE') }) }))] }), _jsxs(Box, { sx: { mt: { mobile: '.75rem', desktop: '.5rem' } }, children: [_jsx(Typography, { variant: 'body1', mb: 0.5, children: text('SET_UP_SMS_CONSENT_Q') }), _jsx(CustomCheckbox, { label: text('SET_UP_SMS_CONSENT'), isChecked: isConsentChecked, onChange: () => {
                            setInSubmit(false);
                            setIsConsentChecked(!isConsentChecked);
                        } }), !isConsentChecked && inSubmit && (_jsx(Box, { children: _jsx(ErrorMessage, { type: 'Plain', message: text('SET_UP_SMS_ERR_CONSENT') }) }))] }), _jsxs(Box, { sx: {
                    mb: 0,
                    display: 'flex',
                    flexDirection: { desktop: 'row', mobile: 'column' },
                    gap: '1rem',
                    mt: { desktop: '6rem' },
                }, children: [_jsx(ActionButton, { type: 'secondary', text: text('SET_UP_SMS_GO_BACK'), onClick: () => goBack() }), _jsx(ActionButton, { type: 'primary', text: text('CONTINUE'), disabled: inSubmit, onClick: () => {
                            handleSubmit();
                        } })] })] }));
};
